import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'overview', title: '1. Security Overview' },
  { id: 'data-protection', title: '2. Data Protection' },
  { id: 'access-control', title: '3. Access Control' },
  { id: 'private-chatbots', title: '4. Private Chatbot Security' },
  { id: 'deployment-security', title: '5. Deployment Security' },
  { id: 'rate-limiting', title: '6. Rate Limiting & Anti-Abuse' },
  { id: 'model-privacy', title: '7. Model Data Retention' },
  { id: 'data-privacy', title: '8. Data Privacy & Compliance' },
  { id: 'best-practices', title: '9. Security Best Practices' },
];

const SecurityCompliancePage = () => {
  return (
    <>
      <NextSeo
        title="Security & Compliance Guide"
        description="Learn about Chat.co's ISO/IEC 27001:2022-certified information security management system, data protection, access control, and privacy safeguards."
        canonical="https://chat.co/docs/security-compliance"
        openGraph={{
          title: 'Security & Compliance Guide | Chat.co Docs',
          description:
            "Learn about Chat.co's ISO/IEC 27001:2022-certified information security management system, data protection, access control, and privacy safeguards.",
          url: 'https://chat.co/docs/security-compliance',
          type: 'article',
          article: {
            publishedTime: '2026-01-20T00:00:00Z',
            modifiedTime: '2026-08-31T00:00:00Z',
            section: 'Troubleshooting',
            tags: [
              'security',
              'compliance',
              'ISO/IEC 27001:2022',
              'information security management system',
              'GDPR',
              'data protection',
              'access control',
              'encryption',
              'privacy',
            ],
          },
          images: [
            {
              url: 'https://chat.co/og-security-compliance.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co Security & Compliance Guide',
            },
          ],
        }}
        twitter={{
          handle: '@chatco',
          site: '@chatco',
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Troubleshooting"
        categoryColor="text-rose-700"
        title="Security & Compliance"
        description="Enterprise-grade security features to protect your data and users."
        readingTime="10 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Citation Quality',
          href: PageLink.docsCitationQuality,
        }}
        nextArticle={{
          title: 'Enterprise SSO',
          href: PageLink.docsEnterpriseSso,
        }}
        keyTakeaways={[
          'SA CYBER LLC’s ISMS supporting Chat.co is certified to ISO/IEC 27001:2022',
          'Data is encrypted at rest and in transit (AES-256, TLS 1.2+)',
          'Role-based access control for team members',
          'Private chatbots with email whitelist authentication',
          'Domain restrictions prevent unauthorized embedding',
          'Rate limiting and anti-abuse protection built-in',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          Security isn&apos;t optional—it&apos;s foundational. Chat.co implements multiple layers of
          protection to keep your data safe, control who can access your chatbots, and ensure
          compliance with privacy regulations. This guide covers the security features available to
          you and how to use them effectively.
        </p>

        {/* Section 1: Overview */}
        <section id="overview" className="scroll-mt-24">
          <h2>1. Security Overview</h2>

          <p>Chat.co uses a defense-in-depth approach with multiple overlapping security layers.</p>

          <div className="my-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:lock" className="text-2xl text-green-600" />
              </div>
              <h4 className="mt-3 font-semibold text-gray-900">Encryption</h4>
              <p className="mt-1 text-sm text-gray-600">Data encrypted in transit and at rest</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                <Icon icon="tabler:shield-check" className="text-2xl text-blue-600" />
              </div>
              <h4 className="mt-3 font-semibold text-gray-900">Access Control</h4>
              <p className="mt-1 text-sm text-gray-600">
                Role-based permissions and authentication
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                <Icon icon="tabler:eye-off" className="text-2xl text-purple-600" />
              </div>
              <h4 className="mt-3 font-semibold text-gray-900">Privacy</h4>
              <p className="mt-1 text-sm text-gray-600">Privacy readiness review in progress</p>
            </div>
          </div>

          <h3>Security Layers</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-sm font-bold text-gray-600">
                1
              </div>
              <div>
                <span className="font-medium text-gray-900">Network Security</span>
                <span className="text-sm text-gray-500"> — HTTPS encryption, security headers</span>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-sm font-bold text-gray-600">
                2
              </div>
              <div>
                <span className="font-medium text-gray-900">Authentication</span>
                <span className="text-sm text-gray-500"> — JWT tokens, OTP, OAuth, API keys</span>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-sm font-bold text-gray-600">
                3
              </div>
              <div>
                <span className="font-medium text-gray-900">Authorization</span>
                <span className="text-sm text-gray-500">
                  {' '}
                  — Role-based access, granular permissions
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-sm font-bold text-gray-600">
                4
              </div>
              <div>
                <span className="font-medium text-gray-900">Data Protection</span>
                <span className="text-sm text-gray-500">
                  {' '}
                  — Encryption, sanitization, audit logging
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Data Protection */}
        <section id="data-protection" className="scroll-mt-24">
          <h2>2. Data Protection</h2>

          <p>Your data is protected using industry-standard encryption and security practices.</p>

          <h3>Encryption</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Data Type
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Protection Method
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Data in transit
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    TLS 1.2+ encryption (HTTPS enforced)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Passwords
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Bcrypt hashing (never stored in plain text)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    API keys
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Scrypt hashing with salt (shown once at creation)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Database
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Encrypted at rest with cloud provider encryption
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Sensitive Data Handling</h3>
          <p>Chat.co automatically protects sensitive data from accidental exposure:</p>
          <ul>
            <li>
              <strong>API keys are never returned</strong> in API responses after creation
            </li>
            <li>
              <strong>Passwords are hashed</strong> before storage and never logged
            </li>
            <li>
              <strong>Response sanitization</strong> automatically removes sensitive fields
            </li>
          </ul>
        </section>

        {/* Section 3: Access Control */}
        <section id="access-control" className="scroll-mt-24">
          <h2>3. Access Control</h2>

          <p>
            Control who can access your account and what they can do with role-based permissions.
          </p>

          <h3>Authentication Methods</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:mail" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Email + OTP</h4>
                <p className="text-sm text-gray-600">
                  Passwordless login with one-time codes sent to your email. Codes expire after a
                  short time and are rate-limited to prevent abuse.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:brand-google" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">Google OAuth</h4>
                <p className="text-sm text-gray-600">
                  Sign in with your Google account for quick, secure authentication.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <Icon icon="tabler:key" className="mt-0.5 text-lg text-gray-500" />
              <div>
                <h4 className="font-medium text-gray-900">API Keys</h4>
                <p className="text-sm text-gray-600">
                  For programmatic access. Keys can be scoped to specific chatbots and have optional
                  expiration dates.
                </p>
              </div>
            </div>
          </div>

          <h3>User Roles</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Role
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Access Level
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Account Owner
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Full access to all features, billing, and team management
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Admin
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Configurable permissions—can manage bots and staff but not billing
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Team Member
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Access only to assigned chatbots with specific permissions
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Granular Permissions</h3>
          <p>Team members can be granted specific permissions per chatbot:</p>
          <div className="my-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700">Chat Logs</div>
            <div className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700">
              Knowledge Base
            </div>
            <div className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700">
              Lead Management
            </div>
            <div className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700">Q&A Training</div>
            <div className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700">Bot Tuning</div>
            <div className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-700">Appearance</div>
          </div>
          <p className="text-sm text-gray-600">
            Each permission can be set to view, add, edit, or delete access.
          </p>
        </section>

        {/* Section 4: Private Chatbots */}
        <section id="private-chatbots" className="scroll-mt-24">
          <h2>4. Private Chatbot Security</h2>

          <p>
            Private chatbots are restricted to authorized users only. Use these features for
            internal tools, employee resources, or customer portals.
          </p>

          <h3>Email Whitelisting</h3>
          <p>Restrict access to specific email addresses or domains.</p>
          <ol>
            <li>Go to your chatbot&apos;s settings</li>
            <li>
              Navigate to <code>Access Control</code>
            </li>
            <li>Enable the email whitelist</li>
            <li>Add authorized email addresses</li>
          </ol>

          <div className="my-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="font-semibold text-gray-900">How it works</h4>
            <ul className="mt-2 space-y-1 text-sm text-gray-600">
              <li>Users must verify their email before accessing the chatbot</li>
              <li>Only emails on the whitelist can proceed</li>
              <li>Email matching is case-insensitive</li>
              <li>You can add individual emails or entire domains</li>
            </ul>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> For internal company bots, whitelist your company domain (e.g.,
              @yourcompany.com) instead of individual emails.
            </p>
          </div>
        </section>

        {/* Section 5: Deployment Security */}
        <section id="deployment-security" className="scroll-mt-24">
          <h2>5. Deployment Security</h2>

          <p>Add extra protection to your chatbot&apos;s deployment links and embeds.</p>

          <h3>Security Options</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                  <Icon icon="tabler:lock" className="text-lg text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Password Protection</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Require a password to access your chatbot. Passwords are securely hashed and
                    rate-limited (5 attempts before temporary lockout).
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:clock" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Link Expiration</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Set deployment links to expire after 1, 7, 30, or 90 days. Perfect for
                    time-limited campaigns or temporary access.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <Icon icon="tabler:list-check" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">IP Whitelist</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Only allow access from specific IP addresses. Ideal for office-only or VPN
                    access requirements.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
                  <Icon icon="tabler:ban" className="text-lg text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">IP Blacklist</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Block specific IP addresses from accessing your chatbot. Useful for stopping
                    abuse or unwanted traffic.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3>Enabling Deployment Security</h3>
          <ol>
            <li>Go to your chatbot&apos;s Deployment page</li>
            <li>Toggle &quot;Enable Deployment Security&quot;</li>
            <li>Configure your desired options</li>
            <li>Save changes</li>
          </ol>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Note:</strong> Security settings apply to all deployment methods—script tags,
              iframes, and direct links all respect these restrictions.
            </p>
          </div>
        </section>

        {/* Section 6: Rate Limiting */}
        <section id="rate-limiting" className="scroll-mt-24">
          <h2>6. Rate Limiting & Anti-Abuse</h2>

          <p>Protect your chatbot from abuse and excessive usage with built-in rate limiting.</p>

          <h3>Types of Rate Limiting</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Type
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Default Limit
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Purpose
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Per-Chatbot
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    20 messages / 2 hours
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Prevents individual users from excessive usage
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    IP-Based
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">Configurable</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Stops abuse from specific IP addresses
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    OTP Requests
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Limited per 15 min
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Prevents OTP brute force attacks
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Password Attempts
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    5 attempts / 15 min lockout
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Prevents password guessing
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    API Key Failures
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    5 failures / 15 min lockout
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Prevents API key guessing
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Custom Rate Limits</h3>
          <p>You can configure per-chatbot rate limits to match your use case:</p>
          <ul>
            <li>
              <strong>Message limit</strong> — Maximum messages per user in the time window
            </li>
            <li>
              <strong>Time window</strong> — Duration before the limit resets
            </li>
            <li>
              <strong>Error message</strong> — Custom message shown when limit is reached
            </li>
          </ul>
        </section>

        {/* Section 7: Model Data Retention */}
        <section id="model-privacy" className="scroll-mt-28">
          <h2>7. Model Data Retention</h2>

          <p>
            Everything above concerns data at rest in Chat.co. This section concerns something teams
            routinely miss: what the <em>model provider</em> does with the prompts and responses
            that pass through them. On a knowledge agent those prompts contain your documents, so
            the answer matters.
          </p>

          <p>
            Every model in the picker under <strong>Tune Bot &rarr; Behavior</strong> carries an
            explicit data-retention badge:
          </p>

          <div className="my-6 overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-900">
                    Badge
                  </th>
                  <th className="border-b border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-900">
                    What it asserts
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="whitespace-nowrap border-b border-gray-100 px-4 py-3 font-medium text-gray-900">
                    No training
                  </td>
                  <td className="border-b border-gray-100 px-4 py-3 text-gray-700">
                    The provider is contractually excluded from training on your prompts and
                    responses. Every model hosted on AWS Bedrock carries this, because
                    Bedrock&apos;s terms exclude customer content from training across all hosted
                    foundation models.
                  </td>
                </tr>
                <tr>
                  <td className="whitespace-nowrap border-b border-gray-100 px-4 py-3 font-medium text-gray-900">
                    No training, shared
                  </td>
                  <td className="border-b border-gray-100 px-4 py-3 text-gray-700">
                    Not trained on, but prompts and responses are shared with the model provider and
                    retained for a limited period for abuse detection, including possible human
                    review.
                  </td>
                </tr>
                <tr>
                  <td className="whitespace-nowrap border-b border-gray-100 px-4 py-3 font-medium text-gray-900">
                    Unknown
                  </td>
                  <td className="border-b border-gray-100 px-4 py-3 text-gray-700">
                    No classification has been made for this route. Direct-API access to OpenAI,
                    Anthropic, Google and xAI sits here &mdash; not as an allegation that training
                    occurs, but because Chat.co withholds a classification until the exact
                    contractual language has been reviewed.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="my-6 rounded-xl border border-primary-100 bg-primary-50 p-5">
            <p className="text-sm leading-relaxed text-primary-900">
              <strong>&ldquo;Unknown&rdquo; means unclassified, not unsafe.</strong> Guessing
              optimistically would mislead you; guessing pessimistically would misrepresent a
              provider. Where your compliance position needs a definite answer, use the{' '}
              <strong>no-training only</strong> filter on the model picker &mdash; it hides
              everything outside the strictest category, including the &ldquo;shared&rdquo; variant,
              because the filter promises the strongest posture rather than an approximation of it.
            </p>
          </div>

          <p>
            Model selection, and the reasoning behind picking one tier over another, is covered in{' '}
            <Link href={PageLink.docsTuneBot} className="text-primary-600 hover:underline">
              tuning your bot
            </Link>
            .
          </p>
        </section>

        {/* Section 8: Data Privacy */}
        <section id="data-privacy" className="scroll-mt-24">
          <h2>8. Data Privacy &amp; Compliance</h2>

          <p>
            Chat.co is designed with privacy in mind and supports compliance with data protection
            regulations.
          </p>

          <h3>ISO/IEC 27001:2022 Certification</h3>
          <div className="my-6 rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-950">
            <div className="flex items-start gap-3">
              <Icon
                icon="tabler:rosette-discount-check"
                className="mt-0.5 text-2xl text-emerald-700"
              />
              <div>
                <h4 className="font-semibold text-emerald-950">
                  SA CYBER LLC&apos;s ISMS supporting Chat.co is certified
                </h4>
                <p className="mt-2">
                  GCAI, an IAS-accredited certification body, assessed the information security
                  management system and certified it to ISO/IEC 27001:2022. The certified scope
                  covers the design, development, hosting, operation, and support of the Chat.co
                  conversational AI platform, including associated data pipelines, API integrations,
                  voice services, and customer support functions.
                </p>
                <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div>
                    <dt className="font-semibold">Certificate</dt>
                    <dd>GCAI-ISMS-LM73</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">Issued</dt>
                    <dd>August 29, 2026</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">Valid through</dt>
                    <dd>August 28, 2029</dd>
                  </div>
                </dl>
                <Link
                  href="/certifications/sa-cyber-iso-27001-2022.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 font-semibold text-emerald-800 underline underline-offset-2 hover:text-emerald-950">
                  View the ISO/IEC 27001:2022 certificate
                  <Icon icon="tabler:external-link" />
                </Link>
              </div>
            </div>
          </div>
          <div className="my-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            Chat.co&apos;s SOC 2 Type II audit remains in progress. The ISO/IEC 27001:2022
            certification does not represent completion of the separate SOC 2 examination.
          </div>

          <h3>GDPR Readiness Features</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <Icon icon="tabler:download" className="text-lg text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Data Export</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Export all your data in JSON or CSV format. Includes conversations, leads, chatbot
                  configurations, and account information.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100">
                <Icon icon="tabler:trash" className="text-lg text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Account Deletion</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Request complete deletion of your account and all associated data. Deletion
                  requests are logged with reason tracking for compliance.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                <Icon icon="tabler:clock" className="text-lg text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Data Retention</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Deleted data is soft-deleted with a 30-day grace period before permanent removal.
                  This allows for recovery if deletion was accidental.
                </p>
              </div>
            </div>
          </div>

          <h3>Audit Logging</h3>
          <p>Chat.co maintains detailed audit logs for accountability and compliance:</p>
          <ul>
            <li>Subscription and billing changes</li>
            <li>Account deletions with reason</li>
            <li>Security events and failed access attempts</li>
            <li>Team member access changes</li>
          </ul>

          <h3>Data Handling Practices</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="flex items-center gap-2 font-medium text-green-900">
                <Icon icon="tabler:check" className="text-green-500" />
                We do
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-green-800">
                <li>Encrypt all data in transit and at rest</li>
                <li>Provide data export on request</li>
                <li>Delete data upon account closure</li>
                <li>Log access for security purposes</li>
              </ul>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h4 className="flex items-center gap-2 font-medium text-red-900">
                <Icon icon="tabler:x" className="text-red-500" />
                We don&apos;t
              </h4>
              <ul className="mt-2 space-y-1 text-sm text-red-800">
                <li>Sell or share data with third parties</li>
                <li>Use your data to train AI models</li>
                <li>Store passwords in plain text</li>
                <li>Access your chatbots without permission</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 8: Best Practices */}
        <section id="best-practices" className="scroll-mt-24">
          <h2>9. Security Best Practices</h2>

          <p>Follow these recommendations to maximize your account security.</p>

          <h3>Account Security Checklist</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Use a strong, unique email</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Your email is your login credential. Use a professional email with good security
                  practices.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Review team access regularly</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Audit who has access to your chatbots quarterly. Remove access for former
                  employees or contractors.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Use minimum necessary permissions</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Grant team members only the permissions they need. A content manager doesn&apos;t
                  need billing access.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Rotate API keys periodically</h4>
                <p className="mt-1 text-sm text-gray-600">
                  If you use API keys, regenerate them every 90 days or immediately if compromised.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">
                  Enable deployment security for sensitive bots
                </h4>
                <p className="mt-1 text-sm text-gray-600">
                  Internal chatbots with confidential information should use password protection or
                  IP restrictions.
                </p>
              </div>
            </div>
          </div>

          <h3>API Key Security</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Do
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Don&apos;t
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Store keys in environment variables
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Commit keys to version control
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Use separate keys for dev/prod
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Share keys across environments
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Set expiration dates on keys
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Leave keys active indefinitely
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Scope keys to specific chatbots
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Use a single key for everything
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Revoke keys when staff leave
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Leave unused keys active
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:help" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">Questions about security?</h4>
                <p className="mt-1 text-primary-800">
                  If you have specific security requirements or questions about compliance, contact
                  our team at{' '}
                  <a href="mailto:security@chat.co" className="underline">
                    security@chat.co
                  </a>
                  . We&apos;re happy to discuss your needs.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <h3>Next Steps</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsWebsiteIntegration}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:code"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Website Integration</h4>
              <p className="mt-1 text-sm text-gray-600">
                Deploy your chatbot securely on your website.
              </p>
            </Link>
            <Link
              href={PageLink.docsMultiBotOrchestration}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:users"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Managing Teams</h4>
              <p className="mt-1 text-sm text-gray-600">
                Set up team access with proper permissions.
              </p>
            </Link>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default SecurityCompliancePage;
